/**
 * Connection Technical Details Component
 *
 * Collapsible section showing technical details for support/troubleshooting
 * Targets secondary persona (Sarah) who needs technical visibility
 *
 * Shows:
 * - Connection UID (copyable)
 * - Platform and environment
 * - External company ID
 * - Token expiration
 * - Auto-refresh status
 * - Version (optimistic locking)
 */

import { useState } from 'react'
import { Collapse, Space, Typography, message } from 'antd'
import {
  DownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import type { Connection } from '@/types/connection.types'
import { getPlatformDisplayName, getPlatformEnvironment } from '@/utils/connectionState'

const { Text } = Typography

/**
 * Props for ConnectionTechnicalDetails component
 */
export interface ConnectionTechnicalDetailsProps {
  /** Connection entity with technical fields */
  connection: Connection

  /** Initial expanded state (default: false) */
  defaultExpanded?: boolean

  /** Custom style */
  style?: React.CSSProperties

  /** Custom class name */
  className?: string
}

/**
 * Copy text to clipboard with user feedback
 */
function copyToClipboard(text: string, label: string): void {
  navigator.clipboard.writeText(text).then(
    () => {
      message.success(`${label} copied to clipboard`)
    },
    () => {
      message.error('Failed to copy to clipboard')
    }
  )
}

/**
 * Format ISO timestamp for display
 */
function formatTimestamp(timestamp?: string): string {
  if (!timestamp) return 'N/A'

  const date = new Date(timestamp)
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Check if token is expiring soon (within 7 days)
 */
function isExpiringSoon(tokenExpiresAt?: string): boolean {
  if (!tokenExpiresAt) return false

  const expiryDate = new Date(tokenExpiresAt)
  const now = new Date()
  const daysUntilExpiry = (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)

  return daysUntilExpiry > 0 && daysUntilExpiry <= 7
}

/**
 * Format time until token expiry
 * Shows days and hours remaining, or "Expired" if past
 */
function formatTimeUntilExpiry(tokenExpiresAt?: string): string {
  if (!tokenExpiresAt) return 'N/A'

  const now = new Date()
  const expiry = new Date(tokenExpiresAt)
  const diffMs = expiry.getTime() - now.getTime()

  if (diffMs <= 0) return 'Expired'

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

  if (days > 0) {
    return `${days} day${days !== 1 ? 's' : ''}, ${hours} hour${hours !== 1 ? 's' : ''}`
  }

  if (hours > 0) {
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
    return `${hours} hour${hours !== 1 ? 's' : ''}, ${minutes} min${minutes !== 1 ? 's' : ''}`
  }

  const minutes = Math.floor(diffMs / (1000 * 60))
  return `${minutes} minute${minutes !== 1 ? 's' : ''}`
}

/**
 * Connection Technical Details
 *
 * Collapsible section showing technical connection details for advanced users
 * and support troubleshooting. Primary users (Debra) can ignore this section.
 *
 * @example
 * ```tsx
 * <ConnectionTechnicalDetails connection={connection} />
 * ```
 *
 * @example Initially expanded
 * ```tsx
 * <ConnectionTechnicalDetails
 *   connection={connection}
 *   defaultExpanded={true}
 * />
 * ```
 */
export const ConnectionTechnicalDetails: React.FC<ConnectionTechnicalDetailsProps> = ({
  connection,
  defaultExpanded = false,
  style,
  className,
}) => {
  const [activeKey, setActiveKey] = useState<string | string[]>(
    defaultExpanded ? ['1'] : []
  )

  const platformName = getPlatformDisplayName(connection.platform)
  const environment = getPlatformEnvironment(connection.platform)
  const tokenExpiring = isExpiringSoon(connection.tokenExpiresAt)

  return (
    <Collapse
      activeKey={activeKey}
      onChange={setActiveKey}
      ghost
      expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
      style={style}
      className={className}
      items={[{
        key: '1',
        label: (
          <Text type="secondary" style={{ fontSize: '13px' }}>
            Technical Details
          </Text>
        ),
        children: (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {/* Connection UID */}
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Connection ID:
              </Text>
              <Space>
                <Text
                  code
                  copyable={{
                    text: connection.uid,
                    onCopy: () => copyToClipboard(connection.uid, 'Connection ID'),
                  }}
                  style={{ fontSize: '12px' }}
                >
                  {connection.uid}
                </Text>
              </Space>
            </Space>

            {/* Platform and Environment */}
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Platform:
              </Text>
              <Text style={{ fontSize: '13px' }}>
                {platformName} ({environment})
              </Text>
            </Space>

            {/* External Company ID */}
            {connection.externalCompanyId && (
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  External ID:
                </Text>
                <Text
                  code
                  copyable={{
                    text: connection.externalCompanyId,
                    onCopy: () =>
                      copyToClipboard(connection.externalCompanyId!, 'External ID'),
                  }}
                  style={{ fontSize: '12px' }}
                >
                  {connection.externalCompanyId}
                </Text>
              </Space>
            )}

            {/* Token Expiration */}
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Token Expires:
              </Text>
              <Space>
                <Text
                  style={{
                    fontSize: '13px',
                    color: tokenExpiring ? 'var(--rp-temporal-orange)' : undefined,
                  }}
                >
                  {formatTimestamp(connection.tokenExpiresAt)}
                </Text>
                {tokenExpiring && (
                  <Text type="warning" style={{ fontSize: '12px' }}>
                    (expiring soon)
                  </Text>
                )}
              </Space>
            </Space>

            {/* Time Until Expiry - Key metric for Square certification demo */}
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Time Until Expiry:
              </Text>
              <Text
                style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: tokenExpiring ? 'var(--rp-temporal-orange)' : formatTimeUntilExpiry(connection.tokenExpiresAt) === 'Expired' ? 'var(--rp-temporal-red)' : 'var(--rp-success)',
                }}
              >
                {formatTimeUntilExpiry(connection.tokenExpiresAt)}
              </Text>
            </Space>

            {/* Auto-refresh Status */}
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Auto-refresh:
              </Text>
              <Space>
                {connection.canAutoRefresh ? (
                  <>
                    <CheckCircleOutlined style={{ color: 'var(--rp-success)' }} />
                    <Text style={{ fontSize: '13px' }}>Enabled</Text>
                  </>
                ) : (
                  <>
                    <CloseCircleOutlined style={{ color: 'var(--rp-temporal-red)' }} />
                    <Text style={{ fontSize: '13px' }}>Disabled</Text>
                  </>
                )}
              </Space>
            </Space>

            {/* Timestamps */}
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Created:
              </Text>
              <Text style={{ fontSize: '13px' }}>
                {formatTimestamp(connection.createdAt)}
              </Text>
            </Space>

            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Last Modified:
              </Text>
              <Text style={{ fontSize: '13px' }}>
                {formatTimestamp(connection.modifiedAt)}
              </Text>
            </Space>

            {/* Version (optimistic locking) */}
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Version:
              </Text>
              <Text code style={{ fontSize: '12px' }}>
                {connection.version}
              </Text>
            </Space>
          </Space>
        ),
      }]}
    />
  )
}

export default ConnectionTechnicalDetails
